import { inject, Injectable } from '@angular/core';
import { MultiselectItem } from '@ui/aos-lib';
import { combineLatest, catchError, of, switchMap, Observable, map, take } from 'rxjs';

import { ReportGroupFilterListingService } from 'src/app/services/api/report-group-filter/report-group-filter-listing.service';
import {
  FilterModel,
  FilterModels
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { EventConfigurationService } from 'src/app/pages/administration/event-configuration/services/event-configuration.service';
import {
  COMMODITY,
  DEVICE_TYPE,
  DURATION,
  EVENT_GROUP,
  EVENT_NAME,
  SEVERITY,
  STATUS
} from '../events-monitor.constants';
import { Store } from '@ngrx/store';
import {
  isSingleCommodityClient,
  selectCommoditiesItems
} from 'src/app/pages/help-and-settings/user-settings/store/user-settings.reducer';

@Injectable({ providedIn: 'root' })
export class EventsMonitorFiltersSourceService {
  readonly #reportGroupService = inject(ReportGroupFilterListingService);
  readonly #eventConfigurationService = inject(EventConfigurationService);
  readonly #store = inject(Store);

  readonly #commodityList = this.#store.select(selectCommoditiesItems);
  readonly #isSingleCommodityClient = this.#store.selectSignal(isSingleCommodityClient);
  readonly #applicable = 'Network Monitoring';

  getAll(): Observable<{ filters: FilterModels; eventsRecord: Record<string, string[]> }> {
    return combineLatest([
      this.#getCommodityFilters(),
      this.getReportGroup(),
      this.#getEventGroupAndEventNameFilters()
    ]).pipe(
      switchMap(([commoditiesFilter, reportGroupFilter, { filters, eventsRecord }]) => {
        return of({
          filters: [
            reportGroupFilter,
            filters.EventGroupName,
            filters.EventName,
            SEVERITY,
            DURATION,
            STATUS,
            ...(this.#isSingleCommodityClient() ? [] : [commoditiesFilter]),
            DEVICE_TYPE
          ],
          eventsRecord
        });
      }),
      catchError(error => {
        return of(error);
      })
    );
  }

  getReportGroup(): Observable<FilterModel> {
    return this.#reportGroupService.getFilter(this.#applicable);
  }

  #getEventGroupAndEventNameFilters(): Observable<{
    filters: { EventGroupName: FilterModel; EventName: FilterModel };
    eventsRecord: Record<string, string[]>;
  }> {
    return this.#eventConfigurationService.getFilterEventNames().pipe(
      map(groups => {
        const groupItems: MultiselectItem[] = [];
        const nameSet = new Set();

        groups.forEach(({ groupName, eventNames }) => {
          groupItems.push({
            text: groupName,
            value: groupName
          });
          nameSet.add(eventNames);
        });
        const nameItems = Array.from(nameSet.values())
          .flat()
          .sort()
          .map(name => ({ text: name, value: name })) as MultiselectItem[];

        return {
          filters: {
            EventGroupName: {
              ...EVENT_GROUP,
              items: groupItems,
              selected: groupItems
            },
            EventName: {
              ...EVENT_NAME,
              items: nameItems,
              selected: nameItems
            }
          },
          eventsRecord: groups.reduce((acc, curr) => ({ ...acc, [curr.groupName]: curr.eventNames }), {})
        };
      })
    );
  }

  #getCommodityFilters(): Observable<FilterModel> {
    return this.#commodityList.pipe(
      map(items => {
        return {
          ...COMMODITY,
          items: items,
          selected: items
        };
      }),
      take(1)
    );
  }
}
